/* Trappbutiken – varukorg som drawer (Wubs) */

const WUBS_CD_SECTION = 'wubs-cart-drawer';
const WUBS_CD_KOPFEL = 'Det gick inte att lägga i varukorgen. Försök igen.';

const wubsCdEscape = (str) =>
  String(str == null ? '' : str).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );

const wubsCdMoney = (cents) =>
  new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(
    (cents || 0) / 100
  );

/* ------------------------------------------------------------------ *
 * 🧨 AVGIFTEN FÖLJER KONFIGURATIONEN (2026-11-03)
 *
 * Startavgift och NCS-tillägg gäller hela konfigurationen men ligger inbakade i priset på EN rad med
 * antal 1. Togs den raden bort försvann avgiften tyst medan resten av konfigurationen låg kvar.
 * Nu läggs ett exemplar av en annan rad i gruppen till med avgiften ovanpå, och den raden minskas
 * med ett. Beslag och tillbehör har samma `_konfig_id` men inga `_components` och räknas inte:
 * utan hyllplan eller ledstång ingen avgift.
 * Svarar med { ny, updates }, eller null när raden kan tas bort som vanligt.
 * ⚠️ Lägg till FÖRST och ta bort sedan, samma ordning som "Ändra": ett fel på vägen ska inte tömma något.
 * 🧨 `ny` är null när raden redan ser ut så: en rad med tre exemplar ligger som 1 st med avgiften + 2 st utan.
 *    Tas 1-raden bort blir den nya raden identisk med den, Shopify slår ihop dem, och `updates` tömde båda
 *    (uppmätt 2026-11-03: båda trösklarna borta). Då räcker det att minska den andra raden med ett.
 * ------------------------------------------------------------------ */
const WUBS_CD_AVGIFTER = ['_Startavgift', '_NCS-tillägg'];

/* "1 000 kr (en gång per konfiguration …)" och "Engångskostnad 900 kr (…)" → kronor. */
function wubsCdAvgiftKr(text) {
  const m = String(text || '').match(/(\d[\d\s\u00a0]*(?:,\d+)?)\s*kr/);
  return m ? parseFloat(m[1].replace(/[\s\u00a0]/g, '').replace(',', '.')) || 0 : 0;
}

const wubsCdKanon = (v) => {
  if (Array.isArray(v)) return v.map(wubsCdKanon);
  if (!v || typeof v !== 'object') return v;
  return Object.keys(v).sort().reduce((ut, k) => { ut[k] = wubsCdKanon(v[k]); return ut; }, {});
};
function wubsCdSammaRad(a, b) {
  try {
    const las = (p) => JSON.stringify(wubsCdKanon({ ...p, _components: JSON.parse(p._components) }));
    return las(a) === las(b);
  } catch {
    return false;
  }
}

async function wubsCdAvgiftsflytt(key) {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error('Varukorgen svarade inte som väntat.');
  const cart = await res.json();
  const p = (cart.items || []).find((i) => i.key === key)?.properties;
  if (!p?._konfig_id) return null;
  const avgifter = WUBS_CD_AVGIFTER.filter((k) => p[k]);
  const kr = avgifter.reduce((summa, k) => summa + wubsCdAvgiftKr(p[k]), 0);
  if (!kr) return null;
  const andra = cart.items.filter((i) => i.key !== key && i.properties?._konfig_id === p._konfig_id && i.properties?._components);
  // En rad som redan bär en avgift är redan låst, så ingen annan rad behöver låsas.
  const syskon = andra.find((i) => WUBS_CD_AVGIFTER.some((k) => i.properties[k])) || andra[0];
  if (!syskon) return null;
  let delar;
  try {
    delar = JSON.parse(syskon.properties._components);
  } catch {
    return null;
  }
  const properties = { ...syskon.properties };
  const ner = delar[0].properties || (delar[0].properties = {});
  avgifter.forEach((k) => { properties[k] = p[k]; });
  // Bara `_Startavgift` följer med ner i komponenten: ordervakten läser den där, se synligaProps i konfiguratorerna.
  if (p._Startavgift) ner._Startavgift = p._Startavgift;
  if (properties.Antal) properties.Antal = '1 st';
  if (ner.Antal) ner.Antal = '1 st';
  if (properties._konfig_rad) {
    properties._konfig_rad = properties._konfig_rad.replace(WUBS_CD_RAD_ANTAL, ' × 1');
    if (p['_NCS-tillägg'] && !properties._konfig_rad.includes(WUBS_CD_RAD_NCS)) properties._konfig_rad += WUBS_CD_RAD_NCS;
  }
  delar[0].price = (parseFloat(delar[0].price) + kr).toFixed(2);
  properties._components = JSON.stringify(delar);
  if (wubsCdSammaRad(properties, p)) return { ny: null, updates: { [syskon.key]: syskon.quantity - 1 } };
  return {
    ny: { id: syskon.variant_id, quantity: 1, properties },
    updates: { [key]: 0, [syskon.key]: syskon.quantity - 1 },
  };
}
window.WubsAvgiftsflytt = wubsCdAvgiftsflytt;

/* ------------------------------------------------------------------ *
 * 🧨 KONFIGURATIONEN FÖLJER KORGEN (2026-11-03)
 *
 * Kunden kan ta bort rader och ändra antal i korgen, men attributet `Konfiguration` och länken bakom
 * "Ändra" skrevs när konfigurationen lades i korgen. Ordern listade rader som aldrig köptes, och "Ändra"
 * tog tillbaka det kunden nyss tagit bort.
 * ------------------------------------------------------------------ */
const WUBS_CD_RAD_ANTAL = / × \d+(?=( · inkl\. NCS-tillägg)?$)/;
const WUBS_CD_RAD_NCS = ' · inkl. NCS-tillägg';

/* Attributet byggs om ur raderna, som bär sin text i `_konfig_rad`. Antalet i texten följer raden, och
   ordningen är den gamla. ⚠️ Rader från före 2026-11-03 saknar texten: ligger en sådan i korgen lämnas
   attributet orört. Ett fel här stoppar inget, attributet är bara en sammanfattning. */
function wubsCdKonfigText(rader, gammal) {
  const gamla = gammal ? gammal.split('||') : [];
  const bas = (t) => t.replace(WUBS_CD_RAD_ANTAL, '').replace(WUBS_CD_RAD_NCS, '');
  const plats = (t) => {
    const i = gamla.findIndex((g) => bas(g) === bas(t));
    return i < 0 ? gamla.length : i;
  };
  /* Ledstångens text har inget antal: den skrivs ut först när kunden ökat i korgen. */
  const text = ({ properties: { _konfig_rad: t }, quantity: q }) =>
    WUBS_CD_RAD_ANTAL.test(t) ? t.replace(WUBS_CD_RAD_ANTAL, ` × ${q}`) : q > 1 ? `${t} × ${q}` : t;
  return rader
    .map(text)
    .sort((a, b) => plats(a) - plats(b))
    .join('||');
}

async function wubsCdSynkaKonfiguration() {
  try {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
    const rader = (cart.items || []).filter((i) => i.properties?._konfig_id && i.properties?._components);
    if (rader.some((i) => !i.properties._konfig_rad)) return;
    const gammal = cart.attributes?.Konfiguration || '';
    const ny = wubsCdKonfigText(rader, gammal);
    if (ny === gammal) return;
    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { Konfiguration: ny } }),
    });
  } catch {
    /* sammanfattningen står kvar som den var */
  }
}
window.WubsKonfigSynk = wubsCdSynkaKonfiguration;

/* Samma format som `koda`/`avkoda` i wubs-konfig-lank.js, som bara laddas på konfiguratorsidorna. */
function wubsCdKoda(obj) {
  const bin = String.fromCharCode(...new TextEncoder().encode(JSON.stringify(obj)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function wubsCdAvkoda(str) {
  const bin = atob(String(str).replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(String(str).length / 4) * 4, '='));
  return JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0))));
}

/* Länken bär hela konfigurationen som den lades i korgen. Vid klick på "Ändra" kortas den till det som ligger
   kvar: varje rad i korgen bär sin plats i länken (`Tröskel`, `Hyllplan`, `Ledstång`), och tillbehören känns
   igen på variant-id. Hyllplanets kant- och hörnval pekar på radens plats och flyttas med.
   Antalet i korgen har inget tak, men konfiguratorn sänker det tyst till sitt (`qm` i länken): 25 st blev
   20 vid nästa köp. Därför delas raden, 20 + 5.
   Svarar med den gamla länken när något inte stämmer eller ingen konfigurerad rad finns kvar. */
const WUBS_CD_RADNYCKEL = { troskel: 'Tröskel', hyllplan: 'Hyllplan', ledstang: 'Ledstång' };
function wubsCdLankEfterKorg(href, items) {
  const u = new URL(href, window.location.origin);
  const id = u.searchParams.get('ers');
  const d = wubsCdAvkoda(u.searchParams.get('k'));
  const nyckel = WUBS_CD_RADNYCKEL[d?.t];
  if (!id || !nyckel) return href;
  const grupp = items.filter((i) => i.properties?._konfig_id === id);
  const antal = new Map();
  grupp.forEach((i) => {
    const plats = parseInt(i.properties[nyckel], 10);
    if (i.properties._components && plats) antal.set(plats, (antal.get(plats) || 0) + i.quantity);
  });
  if (!antal.size) return href;

  if (d.t === 'ledstang') {
    // Ledstången har inget antal i länken: två exemplar av en rad är två likadana ledstänger.
    d.r = (d.r || []).flatMap((r, i) => Array(antal.get(i + 1) || 0).fill(r));
  } else {
    const tak = d.qm > 0 ? d.qm : 20;
    const nyPlats = new Map();
    let radNr = 0;
    d.rows = (d.rows || []).flatMap((r, i) => {
      const delar = [];
      for (let rest = antal.get(i + 1) || 0; rest > 0; rest -= tak) delar.push(Math.min(rest, tak));
      nyPlats.set(i + 1, delar.map(() => ++radNr));
      return delar.map((q) => (Array.isArray(r) ? [r[0], r[1], q] : { ...r, q }));
    });
    if (Array.isArray(d.per)) {
      d.per = d.per.flatMap((e) => (nyPlats.get(e.s) || []).map((s) => ({ ...e, s })));
      // Satt valet bara på den borttagna raden: Avrundad utan vald kant låser annars köpknappen med ett besked om kanter ingen ser.
      if (!d.per.some((e) => Object.values(e.k || {}).some(Boolean))) d.ev = null;
      if (!d.per.some((e) => Object.values(e.h || {}).some(Boolean))) d.cv = null;
    }
  }
  /* Samma variant i två listor ligger som EN rad i korgen: delas ut i länkens ordning, och det kunden
     ökat i korgen läggs på den första. */
  const kvar = new Map();
  grupp.forEach((i) => {
    if (!i.properties._components) kvar.set(String(i.variant_id), (kvar.get(String(i.variant_id)) || 0) + i.quantity);
  });
  const listor = ['br', 'ad', 'acc', 'kon'].filter((k) => Array.isArray(d[k]));
  listor.forEach((k) => {
    d[k] = d[k].flatMap((e) => {
      const v = String(e[e.length - 2]);
      const q = Math.min(Number(e[e.length - 1]) || 0, kvar.get(v) || 0);
      kvar.set(v, (kvar.get(v) || 0) - q);
      return q ? [[...e.slice(0, -1), q]] : [];
    });
  });
  listor.forEach((k) => {
    d[k].forEach((e) => {
      const v = String(e[e.length - 2]);
      e[e.length - 1] += kvar.get(v) || 0;
      kvar.delete(v);
    });
  });
  u.searchParams.set('k', wubsCdKoda(d));
  return u.toString();
}

document.addEventListener('click', async (e) => {
  const a = e.target.closest?.('[data-wubs-konfig-andra]');
  if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  let href = a.href;
  try {
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
    href = wubsCdLankEfterKorg(a.href, cart.items || []);
  } catch {
    /* gamla länken duger: den öppnar konfigurationen som den lades i korgen */
  }
  window.location.href = href;
});

/* ------------------------------------------------------------------ *
 * Smarta upsells
 *
 * Regelmotor som läser varukorgen och föreslår komplement. Den första
 * riktiga regeln blir beslag till ledstång: konfiguratorn rekommenderar
 * ett fäste var 100–120 cm, så antalet = ceil(total längd / 110) + ändfästen.
 *
 * Reglerna är avstängda tills de nya 3D-konfiguratorerna finns och vi vet
 * vad deras line item properties heter. Lägg till dem i SMART_RULES nedan.
 * ------------------------------------------------------------------ */

const WUBS_CD_BRACKET_SPACING_CM = 110;

/** Plockar ut ett cm-mått ur en line item property, t.ex. "240 cm" → 240 */
function wubsCdParseCm(value) {
  if (value == null) return null;
  const m = String(value).match(/(\d+(?:[.,]\d+)?)\s*cm/i) || String(value).match(/^(\d+(?:[.,]\d+)?)$/);
  if (!m) return null;
  const n = parseFloat(m[1].replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Total ledstångslängd i cm för en rad – summerar alla längd-properties */
function wubsCdRailLengthCm(item) {
  const props = item.properties || {};
  let total = 0;
  for (const [key, val] of Object.entries(props)) {
    if (key.startsWith('_')) continue;
    if (!/längd|langd|length|ledstång|ledstang/i.test(key)) continue;
    const cm = wubsCdParseCm(val);
    if (cm) total += cm;
  }
  return total * (item.quantity || 1);
}

/**
 * Reglerna. Varje regel får hela varukorgen och returnerar antingen null
 * eller { variantId, quantity, title, note, price }.
 *
 * Exempel på beslagsregeln (aktivera när konfiguratorn är byggd och
 * BRACKET_VARIANT_ID är känt):
 *
 *   {
 *     id: 'ledstang-beslag',
 *     run(cart) {
 *       const railCm = cart.items
 *         .filter((i) => /ledstång/i.test(i.product_title))
 *         .reduce((sum, i) => sum + wubsCdRailLengthCm(i), 0);
 *       if (!railCm) return null;
 *       const needed = Math.ceil(railCm / WUBS_CD_BRACKET_SPACING_CM) + 1;
 *       const have = cart.items
 *         .filter((i) => i.product_id === BRACKET_PRODUCT_ID)
 *         .reduce((sum, i) => sum + i.quantity, 0);
 *       const missing = needed - have;
 *       if (missing <= 0) return null;
 *       return {
 *         variantId: BRACKET_VARIANT_ID,
 *         quantity: missing,
 *         title: 'Beslag för ledstång',
 *         note: `Du behöver cirka ${needed} beslag till ${Math.round(railCm)} cm ledstång`,
 *       };
 *     },
 *   }
 */
const SMART_RULES = [];

/* ------------------------------------------------------------------ *
 * Drawern
 * ------------------------------------------------------------------ */
class WubsCartDrawer extends HTMLElement {
  connectedCallback() {
    this.overlayBound = false;
    this.bindTriggers();
    this.bindInside();
    this.refreshSmartUpsells();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
    // Kunden lämnar sidan mitt i skrivandet: det som väntar på debouncen ska också iväg.
    window.addEventListener('pagehide', () => {
      if (this.notVantar != null) this.sparaNot(this.notVantar);
    });
  }

  /* --- Öppnare: varukorgsikonen i headern --- */
  bindTriggers() {
    // På varukorgssidan (wubs-cart.liquid) står korgen redan på sidan – ikonen följer sin länk i
    // stället för att öppna samma innehåll i en låda ovanpå (2026-09-14).
    if (document.querySelector('[data-wubs-cart]')) return;
    const link = document.querySelector('#cart-icon-bubble');
    if (!link || link.dataset.wubsCdBound) return;
    link.dataset.wubsCdBound = '1';
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    });
  }

  /* --- Interaktioner inuti drawern --- */
  bindInside() {
    this.addEventListener('click', async (e) => {
      const close = e.target.closest('[data-wubs-cd-close]');
      if (close) return this.close();

      const row = e.target.closest('[data-line]');

      const minus = e.target.closest('[data-wubs-cd-minus]');
      if (minus && row) return this.changeLine(row, this.lineQty(row) - 1);

      const plus = e.target.closest('[data-wubs-cd-plus]');
      if (plus && row) return this.changeLine(row, this.lineQty(row) + 1);

      const remove = e.target.closest('[data-wubs-cd-remove]');
      if (remove && row) return this.changeLine(row, 0);

      const add = e.target.closest('[data-wubs-cd-add]');
      if (add) {
        this.rensaKopFel(add);
        return this.addVariant(add.dataset.wubsCdAdd, 1, add).catch(() => this.visaKopFel(add));
      }
    });

    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-wubs-cd-input]');
      if (input) {
        /* 🧨 TOMT FÄLT ÄR INTE NOLL (2026-11-03). `parseInt('') || 0` gjorde att ett tömt fält och ett klick
           bredvid tog bort raden, även en konfigurerad, utan fråga. Tomt eller ogiltigt = värdet det hade.
           En skriven nolla och minus/ta bort fungerar som förut. */
        const qty = parseInt(input.value, 10);
        if (!Number.isFinite(qty) || qty < 0) {
          input.value = input.defaultValue;
          return;
        }
        this.changeLine(input.closest('[data-line]'), qty);
      }
      const note = e.target.closest('[data-wubs-cd-note]');
      if (note) this.sparaNot(note.value);
    });

    this.addEventListener('input', (e) => {
      const note = e.target.closest('[data-wubs-cd-note]');
      if (!note) return;
      this.notVantar = note.value;
      clearTimeout(this.notTimer);
      this.notTimer = setTimeout(() => this.sparaNot(note.value), 800);
    });
  }

  /* MEDDELANDET FÅR INTE FÖRSVINNA I KASSAKLICKET (2026-11-03). Fältet står utanför kassaformuläret och
     sparades bara av en vanlig fetch på `change`: klicket på kassaknappen ger blur + submit, och
     navigeringen kunde avbryta anropet. Nu går det med keepalive, redan under skrivandet (debounce),
     och fältet hör till formuläret med `form="wubs-cd-form"`, så att kassans POST bär samma text. */
  sparaNot(note) {
    clearTimeout(this.notTimer);
    this.notVantar = null;
    if (note === this.notSkickad) return;
    this.notSkickad = note;
    fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ note }),
    }).catch(() => {
      this.notSkickad = undefined; // nästa ändring ska försöka igen, även med samma text
    });
  }

  lineQty(row) {
    return parseInt(row.querySelector('[data-wubs-cd-input]')?.value, 10) || 0;
  }

  /* --- Öppna/stäng --- */
  open() {
    this.hidden = false;
    WubsScroll.las();   // låser <html>, inte <body> – annars tappar sticky sin scrollport
    // Tvinga fram en reflow så att transitionen startar från stängt läge.
    // (requestAnimationFrame används ofta här, men throttlas när fliken inte målar
    // – då fastnar panelen utanför kanten. Reflow är synkron och alltid pålitlig.)
    void this.offsetWidth;
    this.classList.add('is-open');
    this.querySelector('.wubs-cd__close')?.focus();
  }

  close() {
    if (this.hidden) return;
    this.classList.remove('is-open');
    WubsScroll.slapp();
    // Vänta ut utgångsanimationen
    setTimeout(() => {
      this.hidden = true;
    }, 220);
  }

  setBusy(on) {
    this.classList.toggle('is-busy', !!on);
  }

  /* --- Cart-anrop --- */
  async changeLine(row, quantity) {
    if (!row) return;
    // Enter två gånger går förbi `is-busy`: två avgiftsflyttar hade lagt samma rad två gånger.
    if (quantity === 0 && this.tarBort) return;
    this.tarBort = quantity === 0;
    this.setBusy(true);
    const fot = row.querySelector('.wubs-cd__item-foot');
    this.rensaKopFel(fot);
    try {
      const flytt = quantity === 0 ? await wubsCdAvgiftsflytt(row.dataset.key) : null;
      if (flytt) {
        if (flytt.ny) await this.cartRequest('cart/add.js', { items: [flytt.ny] });
        await this.cartRequest('cart/update.js', { updates: flytt.updates });
      } else {
        await this.cartRequest('cart/change.js', { id: row.dataset.key, quantity });
      }
      await wubsCdSynkaKonfiguration();
    } catch (fel) {
      // Ändringen gick inte igenom: fältet ska visa det som faktiskt ligger i korgen.
      const falt = row.querySelector('[data-wubs-cd-input]');
      if (falt) falt.value = falt.defaultValue;
      this.visaKopFel(fot, fel.beskrivning || 'Det gick inte att ändra antalet. Försök igen.');
    } finally {
      this.tarBort = false;
      this.setBusy(false);
    }
  }

  /**
   * @param {object} [properties] Synliga line item properties (t.ex. { 'Höjd': '185 mm' }).
   *   🔴 Produktformulärets egna fält följde INTE med i korgen förrän den här parametern fanns
   *   (2026-10-06). `wubs-product.js` fångar submit och anropar hit med bara id + antal, så ett
   *   `<input name="properties[…]">` i formuläret postades aldrig – det såg ut att fungera, och
   *   raden blev tyst naken. Skickas inget skickas nyckeln inte alls: oförändrade rader förut.
   */
  async addVariant(id, quantity, btn, properties) {
    if (!id) return;
    btn?.setAttribute('disabled', '');
    this.setBusy(true);
    const rad = { id: Number(id), quantity };
    if (properties && Object.keys(properties).length) rad.properties = properties;
    try {
      await this.cartRequest('cart/add.js', { items: [rad] });
    } finally {
      btn?.removeAttribute('disabled');
      this.setBusy(false);
    }
  }

  /* 🧨 KASTAR NÄR KORGEN SÄGER NEJ (2026-11-03). Förut svalde den 4xx/429 och returnerade tomt, så
     anroparna öppnade lådan utan varan och utan ett ord. Felet bär Shopifys förklaring: `message` för
     konfiguratorernas alert, `beskrivning` bara när Shopify faktiskt gav en.
     ⚠️ UNDANTAG: en uppdatering av BARA attributen (konfiguratorernas sammanfattning "Konfiguration").
     Den går efter att raderna redan kommit med. Ett kast hade sagt "kunde inte lägga i varukorgen" om en
     vara som ligger där, hoppat över städningen av den ersatta gruppen, och nästa försök gett dubbletter. */
  async cartRequest(path, body) {
    const root = window.Shopify?.routes?.root || '/';
    const sammanfattning = path === 'cart/update.js' && body?.attributes && !body?.updates;
    let data = null;
    try {
      const res = await fetch(`${root}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: WUBS_CD_SECTION, sections_url: window.location.pathname }),
      });
      data = await res.json().catch(() => null); // 429 och 5xx kan vara html
      if (!res.ok || !data) {
        const fel = new Error(data?.description || data?.message || 'Varukorgen svarade inte som väntat.');
        fel.status = res.status;
        fel.beskrivning = data?.description || '';
        throw fel;
      }
    } catch (fel) {
      if (sammanfattning) return undefined;
      throw fel;
    }
    this.render(data);
    return data;
  }

  /* --- Besked vid köpknappen (2026-11-03) ---
     Står under knappens rad, inte i den: köprutorna är flex och knappen hade tryckts ihop.
     De smala raderna (tillbehör, upsell) har bara plats i textkolumnen.
     ⚠️ CSS:en ligger i wubs-cart-drawer.css, som laddas på alla sidor med lådan. */
  kopFelAnkare(knapp) {
    const smal = knapp.closest('.wubs-tb__rad, .wubs-cd__upsell')?.querySelector('.wubs-tb__info, .wubs-cd__upsell-body');
    if (smal?.lastElementChild) {
      const sista = smal.lastElementChild;
      return sista.classList.contains('wubs-kopfel') ? sista.previousElementSibling : sista;
    }
    return (
      knapp.closest('.wubs-pdp__buy, .wubs-qa__buy, .wubs-mk__buy, .wubs-prods__actions, .wubs-wl__konfig-actions, .wubs-pdp__sticky-inner, .wubs-cd__item-foot') ||
      knapp
    );
  }

  visaKopFel(knapp, text) {
    if (!knapp) return;
    const ankare = this.kopFelAnkare(knapp);
    let rad = ankare.nextElementSibling;
    if (!rad?.classList.contains('wubs-kopfel')) {
      rad = document.createElement('p');
      rad.className = 'wubs-kopfel';
      rad.setAttribute('role', 'alert');
      ankare.after(rad);
    }
    rad.textContent = text || WUBS_CD_KOPFEL;
    clearTimeout(rad.wubsTimer);
    rad.wubsTimer = setTimeout(() => rad.remove(), 8000);
  }

  rensaKopFel(knapp) {
    if (!knapp) return;
    const rad = this.kopFelAnkare(knapp).nextElementSibling;
    if (rad?.classList.contains('wubs-kopfel')) rad.remove();
  }

  /* --- Rendera om från Section Rendering API --- */
  render(data) {
    const html = data?.sections?.[WUBS_CD_SECTION];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('wubs-cart-drawer');
    if (!fresh) return;

    const wasOpen = !this.hidden;
    /* Svaret kan vara renderat före den senaste texten, och fältet följer med i kassans POST: det kunden
       skrivit ska stå kvar, annars skrev kassan över meddelandet med den gamla texten. */
    const not = this.querySelector('[data-wubs-cd-note]')?.value;
    this.innerHTML = fresh.innerHTML;
    if (not != null) {
      const nyNot = this.querySelector('[data-wubs-cd-note]');
      if (nyNot) nyNot.value = not;
    }
    if (fresh.hasAttribute('data-empty')) this.setAttribute('data-empty', '');
    else this.removeAttribute('data-empty');

    if (wasOpen) {
      this.hidden = false;
      this.classList.add('is-open');
    }

    this.updateHeaderCount(doc);
    this.refreshSmartUpsells();
  }

  /** Håll headerns antal i synk utan att rendera om hela headern */
  updateHeaderCount(doc) {
    const count = doc.querySelector('.wubs-cd__count')?.textContent.trim();
    const iconWrap = document.querySelector('#cart-icon-bubble .wubs-header__tool-icon');
    if (!iconWrap) return;
    let badge = iconWrap.querySelector('.cart-count-bubble');
    if (count) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'wubs-header__badge cart-count-bubble';
        iconWrap.appendChild(badge);
      }
      badge.innerHTML = `<span aria-hidden="true">${wubsCdEscape(count)}</span>`;
    } else if (badge) {
      badge.remove();
    }
  }

  /* --- Smarta upsells --- */
  async refreshSmartUpsells() {
    const slot = this.querySelector('[data-wubs-cd-upsell-slot]');
    const wrap = this.querySelector('[data-wubs-cd-upsells]');
    if (!slot || !wrap || !SMART_RULES.length) return;

    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      const suggestions = SMART_RULES.map((rule) => {
        try {
          return rule.run(cart);
        } catch {
          return null;
        }
      }).filter(Boolean);

      if (!suggestions.length) {
        wrap.hidden = true;
        slot.innerHTML = '';
        return;
      }

      slot.innerHTML = suggestions
        .map(
          (s) => `<li class="wubs-cd__upsell">
            <div class="wubs-cd__upsell-body">
              <span class="wubs-cd__upsell-title">${wubsCdEscape(s.title)}</span>
              ${s.note ? `<span class="wubs-cd__upsell-note">${wubsCdEscape(s.note)}</span>` : ''}
              ${s.price != null ? `<span class="wubs-cd__upsell-price">${wubsCdEscape(wubsCdMoney(s.price))}</span>` : ''}
            </div>
            <button class="wubs-cd__upsell-add" type="button" data-wubs-cd-add="${wubsCdEscape(s.variantId)}">
              Lägg till ${s.quantity > 1 ? s.quantity + ' st' : ''}
            </button>
          </li>`
        )
        .join('');
      wrap.hidden = false;
    } catch {
      wrap.hidden = true;
    }
  }
}

if (!customElements.get('wubs-cart-drawer')) customElements.define('wubs-cart-drawer', WubsCartDrawer);

/* ATC-knappar var som helst på sajten: <button data-wubs-atc="variantId">.
 * Lägger varan i korgen via drawerns addVariant och öppnar den. preventDefault
 * krävs – knapparna sitter ofta inne i länkade kort och får inte följa länken. */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-wubs-atc]');
  if (!btn) return;
  e.preventDefault();
  const drawer = document.querySelector('wubs-cart-drawer');
  if (!drawer) return;
  drawer.rensaKopFel(btn);
  try {
    await drawer.addVariant(btn.dataset.wubsAtc, 1, btn);
  } catch (fel) {
    drawer.visaKopFel(btn);
    return;
  }
  drawer.open();
});

/* TEMAEDITORN (2026-09-18, Liam "var ändras detta?" om inspirationen i tom varukorg). Lådan är stängd
   och ligger utanför sidans sektioner, så att markera "Wubs varukorg" eller ett av dess block visade
   ingenting – man ändrade i blindo. Nu öppnas lådan när sektionen eller ett block markeras och stängs
   när den avmarkeras. shopify:*-händelserna skickas bara av editorn, så inget av detta körs för kunder.
   ⚠️ Vid en ändrad inställning ritar editorn om sektionen: elementet BYTS medan det är öppet och det
   gamla hinner aldrig släppa scroll-låset (djupräknaren i wubs-scroll-lock.js) – därför nollställs det
   innan den nya lådan öppnas, annars satt sidan låst efter att lådan stängts. */
(() => {
  const lada = (e) => {
    const d = document.querySelector('wubs-cart-drawer');
    return d && e.detail && d.dataset.sectionId === e.detail.sectionId ? d : null;
  };
  const oppna = (d) => { if (!d.classList.contains('is-open')) d.open(); };
  document.addEventListener('shopify:section:select', (e) => { const d = lada(e); if (d) oppna(d); });
  document.addEventListener('shopify:section:deselect', (e) => { const d = lada(e); if (d) d.close(); });
  document.addEventListener('shopify:section:load', (e) => {
    const d = lada(e);
    if (!d) return;
    if (window.WubsScroll) window.WubsScroll.slapp(true);
    oppna(d);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const d = lada(e);
    if (!d) return;
    oppna(d);
    setTimeout(() => { if (e.target && e.target.scrollIntoView) e.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, 250);
  });
})();
